import type { GameId } from './game'
import { SCIDChronosOffer } from './offer'
import { SCIDProduct } from './product'

export interface SCIDApplication {
  application: GameId
  account?: {
    tag: string
    name?: string
    progress?: string[]
  }
  eligibility?: {
    purchase?: {
      eligible?: boolean
      reason?: string
    }
  }
}

export interface SCIDAccountProfile {
  accountId: string
  profile?: {
    name?: string
    avatar?: string
    image?: {
      url: string
    }
  }
  identifier?: {
    type: 'EMAIL' | 'PHONE'
    value: string
  }
  applications?: SCIDApplication[]
}

export interface EligibilityStatus {
  isEligible: boolean
  eligibleStatus?: string
  expiredOrNotStarted?: boolean
}

type SCIDShopItemStatusAvailable = {
  status: 'AVAILABLE'
}

type SCIDShopItemStatusClaimInProgress = {
  status: 'CLAIM_IN_PROGRESS'
  claimToken: string
}

type SCIDShopItemStatusClaimComplete = {
  status: 'CLAIM_COMPLETE'
}

type SCIDShopItemStatusDonatePending = {
  status: 'DONATE_PENDING'
  receiverAccountId: string
}

type SCIDShopItemStatusDonateRejected = {
  status: 'DONATE_REJECTED'
  receiverAccountId: string
}

type SCIDShopItemStatusDonateCompleted = {
  status: 'DONATE_COMPLETE'
  receiverAccountId: string
}

type SCIDShopItemStatusReceivePending = {
  status: 'RECEIVE_PENDING'
  donatorAccountId: string
}

type SCIDShopItemStatusReceiveRejected = {
  status: 'RECEIVE_REJECTED'
  donatorAccountId: string
}

type SCIDShopItemStatusReceiveComplete = {
  status: 'RECEIVE_COMPLETE'
  donatorAccountId: string
}

type SCIDShopItemStatusState =
  | SCIDShopItemStatusAvailable
  | SCIDShopItemStatusClaimInProgress
  | SCIDShopItemStatusClaimComplete
  | SCIDShopItemStatusDonatePending
  | SCIDShopItemStatusDonateRejected
  | SCIDShopItemStatusDonateCompleted
  | SCIDShopItemStatusReceivePending
  | SCIDShopItemStatusReceiveRejected
  | SCIDShopItemStatusReceiveComplete

type SCIDShopItemStatus = { timestamp: number } & SCIDShopItemStatusState

type SCIDShopItemType = 'SEASON_PASS' | 'UNKNOWN'

type SCIDShopItemOrigin = 'STORE_PURCHASE' | 'CUSTOMER_SUPPORT'

type SCIDShopItemEligibilityStatus =
  | 'ELIGIBLE'
  | 'NOT_BOUND'
  | 'NOT_ELIGIBLE'
  | 'NOT_FOUND'
  | 'QUOTA_EXCEEDED'
  | 'UNAVAILABLE'

// Ordering used in presentation
export const SCID_SHOP_ITEM_TIERS = ['PREMIUM', 'BASIC', 'DEFAULT', 'EVENT'] as const

export type SCIDShopItemTier = (typeof SCID_SHOP_ITEM_TIERS)[number]

export interface SCIDShopItem {
  id: string
  productId: string
  type: SCIDShopItemType
  applications: string[]
  status: SCIDShopItemStatus
  origin: SCIDShopItemOrigin
  receiptId: string
  accountId: string
  createdAt: number
  donationCount?: number
  rejectionCount?: number
  tier?: SCIDShopItemTier
}

export interface SCIDShopItemEligibility {
  quantity: number
  result: SCIDShopItemEligibilityStatus
}

export type StoreToken = {
  id: string
  email: string
}

export type SCIDResponse<
  T = undefined,
  S extends string = 'bad_request' | 'internal_error' | 'unauthorized',
> = T extends undefined
  ? {
      ok: boolean
      error?: S
    }
  : {
      ok: boolean
      error?: S
      data?: T
    }

export type SCIDPutItemResponse = SCIDResponse<
  undefined,
  'bad_request' | 'internal_error' | 'unauthorized' | 'purchase_receipt_reuse'
>

export type SCIDRevokeItemResponse = SCIDResponse<undefined>

export type SCIDGetInventoryResponse = SCIDResponse<{ items?: SCIDShopItem[] }>

export type SCIDAccountLoginConfirmResponse = SCIDResponse<{
  authToken: string
}>

export type SCIDAccountGetProfileResponse = SCIDResponse<SCIDAccountProfile>

export type SCIDProductResponse = SCIDResponse<{ skus: SCIDProduct[] }>

export type SCIDChronosEventResponse = SCIDResponse<Array<{ gameId: GameId; offers: SCIDChronosOffer[] }>>

export type SCIDAddEventsResponse = SCIDResponse<undefined>

export interface SCIDEvent {
  type: string
  timestamp: number
}

export interface StoreCartUpdatedSCIDEvent extends SCIDEvent {
  type: 'STORE_CART_UPDATED'
  sessionId: string
  currency: string
  locale: string
  action: string
  cartId: string
  cartUrl: string
  cartItems: Array<{
    sku: string
    name: string
    quantity: number
    askingPriceLocal: number
    imageUrl: string
    source: GameId
  }>
}

export interface StoreCheckoutCompletedSCIDEvent extends SCIDEvent {
  type: 'STORE_CHECKOUT_COMPLETED'
  sessionId: string
  currency: string
  locale: string
  transactionReference: string
  paymentMethod: string
  askingPriceUsdCents: number
  cartId: string
  cartUrl: string
  cartItems: Array<{
    sku: string
    name: string
    quantity: number
    askingPriceLocal: number
    imageUrl: string
    source: GameId
  }>
}
